import React, { useEffect, useState } from "react";
import { useAuth } from "src/App/context/AuthContext";
import { useNavigate } from "react-router-dom";
import "./style.scss";

const TAB = { CLIENTS: "clients", MODULES: "modules", LOG: "log" };

const Admin = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState(TAB.CLIENTS);
  const [clients, setClients] = useState([]);
  const [modules, setModules] = useState([]);
  const [accessLog, setAccessLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [newClientEmail, setNewClientEmail] = useState("");
  const [newClientName, setNewClientName] = useState("");
  const [clientSaving, setClientSaving] = useState(false);

  const [newModuleName, setNewModuleName] = useState("");
  const [newModuleUrl, setNewModuleUrl] = useState("");
  const [newModuleDesc, setNewModuleDesc] = useState("");
  const [newModuleType, setNewModuleType] = useState("prototype");
  const [newModuleThumbnail, setNewModuleThumbnail] = useState("");
  const [newModuleAuthUser, setNewModuleAuthUser] = useState("");
  const [newModuleAuthPass, setNewModuleAuthPass] = useState("");
  const [moduleSaving, setModuleSaving] = useState(false);

  const [editingModule, setEditingModule] = useState(null);
  const [editName, setEditName] = useState("");
  const [editUrl, setEditUrl] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editType, setEditType] = useState("prototype");
  const [editThumbnail, setEditThumbnail] = useState("");
  const [editAuthUser, setEditAuthUser] = useState("");
  const [editAuthPass, setEditAuthPass] = useState("");
  const [editSaving, setEditSaving] = useState(false);

  const [selectedClient, setSelectedClient] = useState("");
  const [selectedModule, setSelectedModule] = useState("");
  const [permSaving, setPermSaving] = useState(false);
  const [permMessage, setPermMessage] = useState("");

  const getHeaders = () => ({
    Authorization: `Bearer ${user.session.getIdToken().getJwtToken()}`,
    "Content-Type": "application/json",
  });

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [cRes, mRes, lRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/admin/clients`, { headers: getHeaders() }),
        fetch(`${process.env.REACT_APP_API_URL}/admin/modules`, { headers: getHeaders() }),
        fetch(`${process.env.REACT_APP_API_URL}/admin/access-log`, { headers: getHeaders() }),
      ]);
      const cData = await cRes.json();
      const mData = await mRes.json();
      const lData = await lRes.json();
      setClients(Array.isArray(cData.clients) ? cData.clients : []);
      setModules(Array.isArray(mData.modules) ? mData.modules : []);
      setAccessLog(Array.isArray(lData.log) ? lData.log : []);
    } catch {
      setError("Failed to load data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) fetchAll();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const inviteClient = async () => {
    if (!newClientEmail || !newClientName) return;
    setClientSaving(true);
    try {
      await fetch(`${process.env.REACT_APP_API_URL}/admin/clients`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({ email: newClientEmail, name: newClientName }),
      });
      setNewClientEmail(""); setNewClientName("");
      fetchAll();
    } catch { setError("Failed to invite client."); }
    finally { setClientSaving(false); }
  };

  const createModule = async () => {
    if (!newModuleName || !newModuleUrl) return;
    setModuleSaving(true);
    try {
      await fetch(`${process.env.REACT_APP_API_URL}/admin/modules`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({
          name: newModuleName, url: newModuleUrl, description: newModuleDesc,
          type: newModuleType, thumbnail: newModuleThumbnail,
          basicAuthUser: newModuleAuthUser, basicAuthPass: newModuleAuthPass,
        }),
      });
      setNewModuleName(""); setNewModuleUrl(""); setNewModuleDesc("");
      setNewModuleType("prototype"); setNewModuleThumbnail("");
      setNewModuleAuthUser(""); setNewModuleAuthPass("");
      fetchAll();
    } catch { setError("Failed to create module."); }
    finally { setModuleSaving(false); }
  };

  const startEdit = (mod) => {
    setEditingModule(mod.moduleId);
    setEditName(mod.name);
    setEditUrl(mod.url);
    setEditDesc(mod.description || "");
    setEditType(mod.type || "prototype");
    setEditThumbnail(mod.thumbnail || "");
    setEditAuthUser(mod.basicAuthUser || "");
    setEditAuthPass(mod.hasBasicAuth ? "••••••••" : "");
  };

  const cancelEdit = () => setEditingModule(null);

  const saveEdit = async (moduleId) => {
    setEditSaving(true);
    try {
      await fetch(`${process.env.REACT_APP_API_URL}/admin/modules/${moduleId}`, {
        method: "PUT",
        headers: getHeaders(),
        body: JSON.stringify({
          name: editName, url: editUrl, description: editDesc,
          type: editType, thumbnail: editThumbnail,
          basicAuthUser: editAuthUser, basicAuthPass: editAuthPass,
        }),
      });
      setEditingModule(null);
      fetchAll();
    } catch { setError("Failed to update module."); }
    finally { setEditSaving(false); }
  };

  const deleteModule = async (moduleId) => {
    if (!window.confirm("Delete this module? This cannot be undone.")) return;
    try {
      await fetch(`${process.env.REACT_APP_API_URL}/admin/modules/${moduleId}`, {
        method: "DELETE", headers: getHeaders(),
      });
      fetchAll();
    } catch { setError("Failed to delete module."); }
  };

  const grantAccess = async () => {
    if (!selectedClient || !selectedModule) return;
    setPermSaving(true); setPermMessage("");
    try {
      await fetch(`${process.env.REACT_APP_API_URL}/admin/permissions`, {
        method: "POST", headers: getHeaders(),
        body: JSON.stringify({ userId: selectedClient, moduleId: selectedModule }),
      });
      setPermMessage("Access granted.");
      setTimeout(() => setPermMessage(""), 3000);
    } catch { setPermMessage("Failed to grant access."); }
    finally { setPermSaving(false); }
  };

  const revokeAccess = async (userId, moduleId) => {
    try {
      await fetch(`${process.env.REACT_APP_API_URL}/admin/permissions`, {
        method: "DELETE", headers: getHeaders(),
        body: JSON.stringify({ userId, moduleId }),
      });
      fetchAll();
    } catch { setError("Failed to revoke access."); }
  };

  return (
    <div className="admin-page">
      <div className="admin-header">
        <p className="admin-title">DG ADMIN</p>
        <div className="admin-tabs">
          <span className={tab === TAB.CLIENTS ? "active" : ""} onClick={() => setTab(TAB.CLIENTS)}>CLIENTS</span>
          <span className={tab === TAB.MODULES ? "active" : ""} onClick={() => setTab(TAB.MODULES)}>MODULES</span>
          <span className={tab === TAB.LOG ? "active" : ""} onClick={() => setTab(TAB.LOG)}>ACCESS LOG</span>
        </div>
        <div className="admin-header-right">
          <span className="dashboard-link" onClick={() => navigate("/dashboard")}>DASHBOARD</span>
          <span className="sign-out" onClick={signOut}>sign out</span>
        </div>
      </div>

      {error && <p className="admin-error">{error}</p>}
      {loading && <p className="admin-loading">loading...</p>}

      {!loading && tab === TAB.CLIENTS && (
        <div className="admin-section">
          <div className="form-row">
            <input type="text" placeholder="client name" value={newClientName} onChange={e => setNewClientName(e.target.value)} />
            <input type="text" placeholder="client email" value={newClientEmail} onChange={e => setNewClientEmail(e.target.value)} />
            <button onClick={inviteClient} disabled={clientSaving || !newClientEmail || !newClientName}>INVITE</button>
          </div>
          <div className="list">
            {clients.map(client => (
              <div key={client.userId} className="list-item">
                <div className="list-item-info">
                  <p className="list-item-name">{client.name}</p>
                  <p className="list-item-sub">{client.email}</p>
                </div>
                <div className="list-item-modules">
                  {(client.modules || []).map(mod => (
                    <span key={mod.moduleId} className="module-tag">
                      {mod.name}
                      <span className="revoke" onClick={() => revokeAccess(client.userId, mod.moduleId)}>x</span>
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="grant-section">
            <p className="section-label">GRANT ACCESS</p>
            <div className="form-row">
              <select value={selectedClient} onChange={e => setSelectedClient(e.target.value)}>
                <option value="">select client</option>
                {clients.map(c => <option key={c.userId} value={c.userId}>{c.name}</option>)}
              </select>
              <select value={selectedModule} onChange={e => setSelectedModule(e.target.value)}>
                <option value="">select module</option>
                {modules.map(m => <option key={m.moduleId} value={m.moduleId}>{m.name}</option>)}
              </select>
              <button onClick={grantAccess} disabled={permSaving || !selectedClient || !selectedModule}>GRANT</button>
            </div>
            {permMessage && <p className="perm-message">{permMessage}</p>}
          </div>
        </div>
      )}

      {!loading && tab === TAB.MODULES && (
        <div className="admin-section">
          <div className="form-col">
            <input type="text" placeholder="module name" value={newModuleName} onChange={e => setNewModuleName(e.target.value)} />
            <input type="text" placeholder="url (e.g. https://example.com)" value={newModuleUrl} onChange={e => setNewModuleUrl(e.target.value)} />
            <input type="text" placeholder="description (optional)" value={newModuleDesc} onChange={e => setNewModuleDesc(e.target.value)} />
            <input type="text" placeholder="thumbnail url (optional)" value={newModuleThumbnail} onChange={e => setNewModuleThumbnail(e.target.value)} />
            <div className="form-row">
              <input type="text" placeholder="basic auth username (optional)" value={newModuleAuthUser} onChange={e => setNewModuleAuthUser(e.target.value)} />
              <input type="password" placeholder="basic auth password (optional)" value={newModuleAuthPass} onChange={e => setNewModuleAuthPass(e.target.value)} />
            </div>
            <div className="form-row">
              <select value={newModuleType} onChange={e => setNewModuleType(e.target.value)}>
                <option value="prototype">prototype</option>
                <option value="review">review</option>
                <option value="hub">hub</option>
                <option value="module">module</option>
                <option value="360-visualizer">360-visualizer</option>
              </select>
              <button onClick={createModule} disabled={moduleSaving || !newModuleName || !newModuleUrl}>CREATE</button>
            </div>
          </div>
          <div className="list">
            {modules.map(mod => (
              <div key={mod.moduleId} className="list-item">
                {mod.thumbnail && editingModule !== mod.moduleId && (
                  <div className="list-item-thumbnail">
                    <img src={mod.thumbnail} alt={mod.name} />
                  </div>
                )}
                {editingModule === mod.moduleId ? (
                  <div className="edit-form">
                    <input type="text" value={editName} onChange={e => setEditName(e.target.value)} placeholder="name" />
                    <input type="text" value={editUrl} onChange={e => setEditUrl(e.target.value)} placeholder="url" />
                    <input type="text" value={editDesc} onChange={e => setEditDesc(e.target.value)} placeholder="description" />
                    <input type="text" value={editThumbnail} onChange={e => setEditThumbnail(e.target.value)} placeholder="thumbnail url" />
                    <input type="text" value={editAuthUser} onChange={e => setEditAuthUser(e.target.value)} placeholder="basic auth username" />
                    <input type="password" value={editAuthPass} onChange={e => setEditAuthPass(e.target.value)} placeholder="basic auth password (leave masked to keep existing)" />
                    <select value={editType} onChange={e => setEditType(e.target.value)}>
                      <option value="prototype">prototype</option>
                      <option value="review">review</option>
                      <option value="hub">hub</option>
                      <option value="module">module</option>
                      <option value="360-visualizer">360-visualizer</option>
                    </select>
                    <div className="edit-actions">
                      <button onClick={() => saveEdit(mod.moduleId)} disabled={editSaving}>SAVE</button>
                      <button onClick={cancelEdit}>CANCEL</button>
                    </div>
                  </div>
                ) : (
                  <div className="list-item-info">
                    <p className="list-item-name">{mod.name}</p>
                    <p className="list-item-sub">{mod.url}</p>
                    {mod.description && <p className="list-item-desc">{mod.description}</p>}
                    {mod.hasBasicAuth && <p className="list-item-auth">🔒 basic auth set</p>}
                  </div>
                )}
                {editingModule !== mod.moduleId && (
                  <div className="list-item-actions">
                    <p className="list-item-type">{mod.type}</p>
                    <button onClick={() => startEdit(mod)}>EDIT</button>
                    <button className="btn-danger" onClick={() => deleteModule(mod.moduleId)}>DELETE</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {!loading && tab === TAB.LOG && (
        <div className="admin-section">
          <table className="access-log-table">
            <thead>
              <tr>
                <th>CLIENT</th>
                <th>EMAIL</th>
                <th>MODULE</th>
                <th>VIEWS</th>
                <th>LAST VIEWED</th>
              </tr>
            </thead>
            <tbody>
              {accessLog.length === 0 && (
                <tr><td colSpan="5" className="log-empty">No access records yet.</td></tr>
              )}
              {accessLog.map((entry, i) => (
                <tr key={i}>
                  <td>{entry.name || "—"}</td>
                  <td>{entry.email}</td>
                  <td>{entry.moduleName}</td>
                  <td>{entry.viewCount}</td>
                  <td>{new Date(entry.lastViewed).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Admin;
